/**
 * REGISTRO DE ERROS DE TELA (rodada FUNDAÇÕES, A1 — 17/08/2026).
 *
 * Quando o front quebra — erro de JS não tratado, promessa rejeitada,
 * componente que falha ao renderizar — o servidor fica sabendo, sem depender
 * de o vendedor avisar.
 *
 * Regras duras (do handoff):
 *  · O registro NUNCA derruba a tela: todo caminho tem catch vazio — o
 *    mecanismo de erro não pode causar erro.
 *  · SEM dado sensível: só mensagem/stack/rota (o servidor re-sanitiza:
 *    pathname puro, IDs anonimizados, tamanhos truncados). Nenhum valor de
 *    formulário passa por aqui por construção — nada lê o DOM.
 *  · Erros de REDE do próprio usuário (wifi caiu) são classificados 'rede'
 *    e marcados separados no servidor — não inundam o registro.
 *  · Throttle local: o MESMO erro (mesma chave) só é reenviado depois de
 *    60s — um loop de render não vira metralhadora.
 */

const enviadosRecentemente = new Map<string, number>();
const THROTTLE_MS = 60_000;

export type TipoErroTela = 'js' | 'promessa' | 'render' | 'rede';

/** Padrões de falha de rede do lado do usuário (Chrome/Firefox/Safari). */
const PADROES_REDE = /failed to fetch|networkerror|load failed|err_internet|err_network|fetch failed|timeout|abortError/i;

export function classificarErro(mensagem: string, tipoBase: Exclude<TipoErroTela, 'rede'>): TipoErroTela {
  return PADROES_REDE.test(mensagem) ? 'rede' : tipoBase;
}

function navegadorResumido(): string {
  try {
    const ua = navigator.userAgent;
    const m =
      ua.match(/(Edg|Chrome|Firefox|Safari)\/([\d.]+)/)?.slice(1, 3).join(' ') ?? 'desconhecido';
    return `${m}${/Mobile|Android|iPhone/.test(ua) ? ' (mobile)' : ''}`.slice(0, 120);
  } catch {
    return 'desconhecido';
  }
}

export function reportarErroDeTela(opts: {
  tipo: Exclude<TipoErroTela, 'rede'>;
  mensagem: string;
  stack?: string | null;
}): void {
  try {
    const mensagem = String(opts.mensagem ?? 'erro sem mensagem').slice(0, 500);
    const tipo = classificarErro(mensagem, opts.tipo);
    const rota = window.location.pathname;
    const chave = `${tipo}|${mensagem.slice(0, 120)}|${rota}`;
    const agora = Date.now();
    const ultimo = enviadosRecentemente.get(chave);
    if (ultimo && agora - ultimo < THROTTLE_MS) return;
    enviadosRecentemente.set(chave, agora);
    // Mapa nunca cresce sem limite (sessões longas).
    if (enviadosRecentemente.size > 200) enviadosRecentemente.clear();

    // fetch direto (não o api client): se o erro FOR do api client, usar o
    // client pra reportar entraria em loop. keepalive envia mesmo se a
    // pessoa navegar/fechar logo depois.
    void fetch('/api/v1/erros-de-tela', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...cabecalhoAuth() },
      body: JSON.stringify({
        tipo,
        mensagem,
        stack: opts.stack?.slice(0, 4000) ?? null,
        rota,
        navegador: navegadorResumido(),
      }),
      keepalive: true,
    }).catch(() => {
      // Silêncio absoluto — regra do handoff.
    });
  } catch {
    // Idem: o registro jamais derruba a tela.
  }
}

/** Token atual do app (memória do api client) — sem tocar em storage. */
let obterToken: (() => string | null) | null = null;
export function conectarTokenParaRegistroDeErros(fn: () => string | null): void {
  obterToken = fn;
}
function cabecalhoAuth(): Record<string, string> {
  try {
    const t = obterToken?.();
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

let instalado = false;

/** Chamado uma vez no boot do app (main.tsx). */
export function instalarRegistroDeErros(): void {
  if (instalado) return;
  instalado = true;
  try {
    window.addEventListener('error', (ev) => {
      reportarErroDeTela({
        tipo: 'js',
        mensagem: ev.message || String(ev.error ?? 'erro'),
        stack: (ev.error as Error | undefined)?.stack ?? null,
      });
    });
    window.addEventListener('unhandledrejection', (ev) => {
      const razao = ev.reason as Error | string | undefined;
      reportarErroDeTela({
        tipo: 'promessa',
        mensagem: typeof razao === 'string' ? razao : (razao?.message ?? 'promessa rejeitada'),
        stack: typeof razao === 'object' ? (razao?.stack ?? null) : null,
      });
    });
  } catch {
    // nunca derruba
  }
}
